/**
 * Admin access gating (008 T017/T018, 006).
 *
 * UI gating is convenience; the server is the source of truth and enforces on
 * every gated call. These helpers mirror the server's role/tenant rules so the
 * UI hides what the user cannot access and never leaks cross-tenant data.
 */
import type { Role } from "@genz/platform";

export type AdminSection =
  | "customers"
  | "activation"
  | "entitlements"
  | "providers"
  | "prompts"
  | "approvals"
  | "appearance"
  | "segmenter"
  | "image_library"
  | "workspace_defaults"
  | "hosting";

/** Sections only a platform super-admin may see (cross-tenant / global config).
 *
 *  `providers` is NOT here since WS-HF shared login: a tenant admin opens it to see the company
 *  Higgsfield card read-only ("Only a super-admin can change this."). `ProvidersTab` renders
 *  nothing else for them, and every provider route stays super-admin only on the server. */
const SUPER_ONLY: ReadonlySet<AdminSection> = new Set([
  "prompts",
  "appearance",
  "segmenter",
  "image_library",
  "workspace_defaults",
  "hosting",
]);

export function canAccessAdmin(role: Role | undefined): boolean {
  return role === "tenant_admin" || role === "super_admin";
}

export function canAccessSection(role: Role | undefined, section: AdminSection): boolean {
  if (!canAccessAdmin(role)) return false;
  if (SUPER_ONLY.has(section)) return role === "super_admin";
  return true;
}

/** Whether `viewerRole` in `viewerTenant` may read data for `targetTenant`. */
export function canViewTenant(
  viewerRole: Role | undefined,
  viewerTenant: string | undefined,
  targetTenant: string,
): boolean {
  if (viewerRole === "super_admin") return true; // cross-tenant (audited server-side)
  if (viewerRole === "tenant_admin") return viewerTenant === targetTenant;
  return false;
}

export function visibleSections(role: Role | undefined): AdminSection[] {
  const all: AdminSection[] = [
    "customers",
    "activation",
    "entitlements",
    "providers",
    "prompts",
    "approvals",
    "appearance",
    "segmenter",
    "image_library",
    "workspace_defaults",
    /* WS-Z5 (R5). Hosting/CDN was built, tested and mounted nowhere for two waves. It is the
       ONLY client of `/api/admin/hosting/settings` and of the R2 credentials the signed-upload
       broker reads, so there was no surviving control to point at — Rule A ("remove
       redundancy, never capability") makes that a mount, not a delete. Last, and
       super-admin-only, because hosting is platform-global. */
    "hosting",
  ];
  return all.filter((s) => canAccessSection(role, s));
}
